import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, Card, CardContent, Chip, Grid, Skeleton, TextField, Typography } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import UpdateChangeRequests from './UpdateChangeRequests';
import { styles } from '../ModifyChangeRequest/styles';
import { hasPermission } from '../../../utils/permission';
import minorCategoryIcon from '../../../assets/images/minorCategory.svg';
import majorCategoryIcon from '../../../assets/images/majorCategory.svg';
import PositionStatusChip from '../../../features/positions/Positions/PositionStatusChip';
import { StatusBackgroundColors } from '../columns';
import ActionButton from '../../../components/common/ActionButtonAndMenu/ActionButton';
import { ChangeRequestMenu } from '../../../features/changeRequests/ChangeRequestMenu';
import { useSnackbar } from 'notistack';
import {
    useDisplayChangeRequestMutation,
    useDisplayChangeRequestWithCompaFieldsMutation,
} from '../../../api/changeRequests';
import PositionCodesDisplay, { getPositionsInRequisition } from '../PositionCodesDisplay';
import { getCategoryDisplayName, isOneDemandCategory } from '../categoryUtils';

export const ChangeRequestDetails = (changeRequest) => {
    const positionStatus = useMemo(() => {
        const statusAttributes = changeRequest?.changeRequest?.attributes?.find((item) => item.name === 'status');
        if (statusAttributes) {
            return statusAttributes?.currentValue;
        }
        return changeRequest?.changeRequest?.position?.status;
    }, [changeRequest?.changeRequest]);

    const positionsInRequisition = useMemo(
        () =>
            changeRequest?.positionsInRequisition ||
            getPositionsInRequisition(changeRequest) ||
            getPositionsInRequisition(changeRequest?.changeRequest),
        [changeRequest],
    );

    return (
        <Grid>
            <Grid container spacing={2} direction="column" alignItems="stretch">
                {positionsInRequisition ? (
                    <Grid item container spacing={2}>
                        <Grid item xs={12} display={'grid'}>
                            <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                                Position Code:
                            </Typography>
                            <PositionCodesDisplay
                                positionsInRequisition={positionsInRequisition}
                                fallbackCode={changeRequest?.changeRequest?.position?.positionCode}
                            />
                        </Grid>
                    </Grid>
                ) : null}
                <Grid item container spacing={2}>
                    {!positionsInRequisition ? (
                        <Grid item xs={3} display={'grid'}>
                            <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                                Position Code:
                            </Typography>
                            <Typography sx={styles.text} color="textPrimary" variant="body2">
                                {changeRequest?.changeRequest?.position?.positionCode || '-'}
                            </Typography>
                        </Grid>
                    ) : null}
                    <Grid item xs={3} display={'grid'}>
                        <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                            Position Status:
                        </Typography>
                        <Box>{positionStatus ? <PositionStatusChip label={positionStatus} /> : '-'}</Box>
                    </Grid>
                    <Grid item xs={3} display={'grid'}>
                        <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                            Change Policy Name:
                        </Typography>
                        <Typography sx={styles.text} color="textPrimary" variant="body2">
                            {changeRequest?.changeRequest?.position?.changeRequestPolicy?.name || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={3} display={'grid'}>
                        <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                            Category:
                        </Typography>
                        <Typography sx={styles.text} color="textPrimary" variant="body2">
                            {isOneDemandCategory(changeRequest?.changeRequest?.categoryType?.name) ? (
                                <AccountBalanceIcon
                                    sx={{ fontSize: 18, color: '#ed7342', mr: 0.75, verticalAlign: 'middle' }}
                                    aria-hidden
                                />
                            ) : (
                                <Box
                                    component="img"
                                    src={
                                        changeRequest?.changeRequest?.categoryType?.name === 'Minor'
                                            ? minorCategoryIcon
                                            : majorCategoryIcon
                                    }
                                    alt={changeRequest?.changeRequest?.categoryType?.name}
                                    sx={{
                                        width: 18,
                                        height: 18,
                                        mr: 0.75,
                                        verticalAlign: 'middle',
                                    }}
                                />
                            )}
                            {changeRequest?.changeRequest?.categoryType?.name
                                ? getCategoryDisplayName(changeRequest.changeRequest.categoryType.name)
                                : null}
                        </Typography>
                    </Grid>
                    {positionsInRequisition ? <Grid item xs={3} display={'grid'} /> : null}
                </Grid>
                <Grid item container spacing={2}>
                    <Grid item xs={3} display={'grid'}>
                        <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                            Change request Status:
                        </Typography>
                        <Box>
                            <Chip
                                label={changeRequest?.changeRequest?.status}
                                sx={{
                                    backgroundColor:
                                        StatusBackgroundColors[changeRequest?.changeRequest?.status?.toLowerCase()],
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={3} display={'grid'}>
                        <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                            Request Submitted By:
                        </Typography>
                        <Typography sx={styles.text} color="textPrimary" variant="body2">
                            {changeRequest?.changeRequest?.createdBy?.employee.fullName
                                ? changeRequest?.changeRequest?.createdBy?.employee.fullName
                                : '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={3} display={'grid'}>
                        <Typography sx={styles.text} color="textSecondary" variant="subtitle2">
                            Request Submitted On:
                        </Typography>
                        <Typography sx={styles.text} color="textPrimary" variant="body2">
                            {changeRequest?.changeRequest?.createdOn
                                ? moment(changeRequest?.changeRequest?.createdOn).format('MMM DD yyyy')
                                : '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={3} display={'grid'} />
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <UpdateChangeRequests changeRequest={changeRequest} />
            </Grid>
            <Grid item sx={{ pt: 2 }} xs={12}>
                <TextField
                    fullWidth
                    label="Comment"
                    size="small"
                    variant="outlined"
                    value={changeRequest?.changeRequest?.comments}
                    disabled
                    multiline
                    rows={5}
                />
            </Grid>
        </Grid>
    );
};

const ViewChangeRequests = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isApproverCR, setIsApproverCR] = useState(false);
    const [changeRequest, setChangeRequest] = useState();
    const authorities = useSelector((state) => state.session.user.authorities);
    const userHasCompaDisplayPermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_VIEW_CHANGE_REQUEST',
    ]);

    useEffect(() => {
        if (searchParams) {
            if (searchParams.get('type')) searchParams.get('type') === 'approver' && setIsApproverCR(true);
            else setSearchParams({ type: 'all' });
        }
    }, [searchParams]);

    const [displayChangeRequest, { isLoading: isDisplayLoading }] = useDisplayChangeRequestMutation();
    const [displayChangeRequestWithCompaFields, { isLoading: isDisplayWithCompaLoading }] =
        useDisplayChangeRequestWithCompaFieldsMutation();

    useEffect(() => {
        if (searchParams && searchParams.get('type')) {
            const getCRDetails = async () => {
                try {
                    const bodyFormData = new FormData();
                    bodyFormData.append('id', params.id);

                    const response =
                        isApproverCR || userHasCompaDisplayPermisson
                            ? await displayChangeRequestWithCompaFields(bodyFormData).unwrap()
                            : await displayChangeRequest(bodyFormData).unwrap();

                    if (!response) return;
                    if (response?.status === 'ERROR') throw new Error(response?.message);

                    setChangeRequest(response);
                } catch (error) {
                    if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                    else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
                }
            };
            getCRDetails();
        }
    }, [userHasCompaDisplayPermisson, isApproverCR, searchParams]);

    const isLoading = isDisplayLoading || isDisplayWithCompaLoading;

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">{'Change Request Details'}</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        {isLoading || !(searchParams && searchParams.get('type')) ? (
                            <Box
                                sx={{
                                    py: 4,
                                }}
                            >
                                <Skeleton height={42} />
                                <Skeleton />
                                <Skeleton />
                            </Box>
                        ) : (
                            <>
                                <Grid container direction="column" spacing={2}>
                                    <Grid
                                        item
                                        sx={{
                                            display: 'flex',
                                            pb: 2,
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Button
                                            startIcon={<ChevronLeftIcon />}
                                            variant="text"
                                            color="secondary"
                                            onClick={() =>
                                                searchParams.get('type') === 'approver'
                                                    ? navigate('/changerequests?type=approval-requests')
                                                    : navigate('/changerequests?type=change-requests')
                                            }
                                        >
                                            Back
                                        </Button>
                                        <ChangeRequestMenu
                                            Component={ActionButton}
                                            row={{
                                                ...changeRequest?.changeRequest,
                                                positions_in_requisition:
                                                    changeRequest?.positions_in_requisition ??
                                                    changeRequest?.positionsInRequisition ??
                                                    changeRequest?.changeRequest?.positions_in_requisition ??
                                                    changeRequest?.changeRequest?.positionsInRequisition,
                                            }}
                                            tab={searchParams.get('type')}
                                            handleAction={() =>
                                                searchParams.get('type') === 'approver'
                                                    ? navigate('/changerequests?type=approval-requests')
                                                    : navigate('/changerequests?type=change-requests')
                                            }
                                        />
                                    </Grid>
                                </Grid>
                                <ChangeRequestDetails
                                    changeRequest={changeRequest?.changeRequest}
                                    positionsInRequisition={getPositionsInRequisition(changeRequest)}
                                />
                            </>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default ViewChangeRequests;
